/**
 * DBSCAN (haversine distance on WGS84 lat/lng).
 *
 * **Border points and relabeled NOISE do not expand clusters.** In canonical DBSCAN, only
 * *core* points add neighbors to the seed queue. Border points are density-reachable but
 * not core; they must not run `regionQuery` to grow the cluster. Here, a point first
 * labeled NOISE that is later reached from a core’s queue is treated as border: we relabel
 * it into the active cluster but skip its own `regionQuery` (see the `else if` after the
 * NOISE relabel). Do not change this loop without an agreed spec — past refactors that let
 * border/relabeled NOISE expand contradicted each other in review.
 */
export interface Point {
  lat: number;
  lng: number;
}

export interface DBSCANResult {
  clusters: number[][];
  noise: number[];
}

const DEG_TO_RAD = Math.PI / 180;
const EARTH_RADIUS_KM = 6371;

function haversineKm(a: Point, b: Point): number {
  const dLat = (b.lat - a.lat) * DEG_TO_RAD;
  const dLng = (b.lng - a.lng) * DEG_TO_RAD;
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h = sinLat * sinLat + Math.cos(a.lat * DEG_TO_RAD) * Math.cos(b.lat * DEG_TO_RAD) * sinLng * sinLng;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
}

export function dbscan(points: Point[], epsilonKm: number, minPoints: number): DBSCANResult {
  const n = points.length;
  const clusterIds = new Int32Array(n).fill(-1); // -1 = unvisited
  const NOISE = -2;
  let currentCluster = 0;

  function regionQuery(idx: number): number[] {
    const neighbors: number[] = [];
    for (let i = 0; i < n; i++) {
      if (haversineKm(points[idx], points[i]) <= epsilonKm) {
        neighbors.push(i);
      }
    }
    return neighbors;
  }

  for (let i = 0; i < n; i++) {
    if (clusterIds[i] !== -1) continue;

    const neighbors = regionQuery(i);

    if (neighbors.length < minPoints) {
      clusterIds[i] = NOISE;
      continue;
    }

    clusterIds[i] = currentCluster;
    const seeds = new Set(neighbors);
    seeds.delete(i);
    const queue = [...seeds];
    let head = 0;

    while (head < queue.length) {
      const q = queue[head]!;
      head += 1;

      // If q was NOISE, relabel into this cluster, then skip its regionQuery (border / non-core).
      // Use `else if` so we do not hit a separate `if (assigned) continue` that would skip the relabel path.
      if (clusterIds[q] === NOISE) {
        clusterIds[q] = currentCluster;
      } else if (clusterIds[q] !== -1) {
        continue;
      }

      clusterIds[q] = currentCluster;
      const qNeighbors = regionQuery(q);

      if (qNeighbors.length >= minPoints) {
        for (const nb of qNeighbors) {
          if (!seeds.has(nb)) {
            seeds.add(nb);
            queue.push(nb);
          }
        }
      }
    }

    currentCluster++;
  }

  const clusters: number[][] = [];
  const noise: number[] = [];

  for (let i = 0; i < n; i++) {
    if (clusterIds[i] === NOISE) {
      noise.push(i);
    } else {
      while (clusters.length <= clusterIds[i]) clusters.push([]);
      clusters[clusterIds[i]].push(i);
    }
  }

  return { clusters, noise };
}
